import { ReconLabClient } from '../client';
import { SearchHistory, ApiResponse, DeleteHistoryOptions } from '../types';

/**
 * Search history module
 */
export class HistoryModule {
  private client: ReconLabClient;

  /**
   * Create a new history module
   * @param client The API client
   */
  constructor(client: ReconLabClient) {
    this.client = client;
  }

  /**
   * Get search history for a wallet
   * @param walletAddress The wallet address
   * @param limit Maximum number of entries to return
   * @returns Promise resolving to search history
   */
  async getHistory(walletAddress: string, limit?: number): Promise<{
    data: SearchHistory[];
    total: number;
    showing: number;
  }> {
    const params: Record<string, any> = { walletAddress };
    if (limit) {
      params.limit = limit;
    }

    const response = await this.client.get<SearchHistory[]>('/api/history', params);
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch search history');
    }
    
    return {
      data: response.data,
      total: response.data.length,
      showing: response.data.length,
    };
  }

  /**
   * Get recent searches for a wallet
   * @param walletAddress The wallet address
   * @param count Number of recent searches to return
   * @returns Promise resolving to recent searches
   */
  async getRecentSearches(walletAddress: string, count = 5): Promise<SearchHistory[]> {
    const history = await this.getHistory(walletAddress, count);
    return history.data;
  }

  /**
   * Get successful searches for a wallet
   * @param walletAddress The wallet address
   * @param limit Maximum number of entries to return
   * @returns Promise resolving to successful searches
   */
  async getSuccessfulSearches(walletAddress: string, limit?: number): Promise<SearchHistory[]> {
    const history = await this.getHistory(walletAddress, limit);
    return history.data.filter(entry => entry.status === 'success');
  }

  /**
   * Get failed searches for a wallet
   * @param walletAddress The wallet address
   * @param limit Maximum number of entries to return
   * @returns Promise resolving to failed searches
   */
  async getFailedSearches(walletAddress: string, limit?: number): Promise<SearchHistory[]> {
    const history = await this.getHistory(walletAddress, limit);
    return history.data.filter(entry => entry.status === 'error');
  }

  /**
   * Get searches by query for a wallet
   * @param walletAddress The wallet address
   * @param query The query to search for
   * @param limit Maximum number of entries to return
   * @returns Promise resolving to matching searches
   */
  async getSearchesByQuery(walletAddress: string, query: string, limit?: number): Promise<SearchHistory[]> {
    const history = await this.getHistory(walletAddress, limit);
    return history.data.filter(entry => 
      entry.query.toLowerCase().includes(query.toLowerCase())
    );
  }

  /**
   * Delete a specific history entry or clear all history
   * @param options The deletion options
   * @returns Promise resolving to the deletion result
   */
  async deleteHistory(options: DeleteHistoryOptions): Promise<{
    success: boolean;
    message: string;
    deletedCount?: number;
  }> {
    const response = await this.client.delete<{
      success: boolean;
      message: string;
      deletedCount?: number;
    }>('/api/history', { data: options });
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to delete history');
    }
    
    return response.data;
  }

  /**
   * Delete a specific history entry
   * @param walletAddress The wallet address
   * @param historyId The ID of the history entry to delete
   * @returns Promise resolving to the deletion result
   */
  async deleteHistoryEntry(walletAddress: string, historyId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.deleteHistory({
      walletAddress,
      historyId,
    });
  }

  /**
   * Clear all search history for a wallet
   * @param walletAddress The wallet address
   * @returns Promise resolving to the deletion result
   */
  async clearHistory(walletAddress: string): Promise<{
    success: boolean;
    message: string;
    deletedCount: number;
  }> {
    const result = await this.deleteHistory({
      walletAddress,
      clearAll: true,
    });
    
    return {
      success: result.success,
      message: result.message,
      deletedCount: result.deletedCount || 0,
    };
  }
}
